"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import type { TypedNotificationsResponse } from "@/types/feed.types";
import type { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
interface UseNotificationsQueryProps {
  /**
   * If true, reverse the notifications order (e.g. for a "descending" / "newest first" display).
   */
  readonly reverse?: boolean | undefined;

  /**
   * Only fetch notifications if we have a valid identity.
   * Used in "enabled" to avoid sending queries prematurely.
   */
  readonly identity?: string | null | undefined;

  /**
   * Example usage where you only fetch if no active profile proxy is set.
   * Adjust or remove according to your own logic.
   */
  readonly activeProfileProxy?: boolean | undefined;

  /**
   * How many notifications to fetch per page.
   */
  readonly limit?: string | undefined;

  /**
   * The cause of the notifications to fetch.
   */
  readonly cause?: ApiNotificationCause[] | null | undefined;
}
type NotificationsQueryParams = {
  limit: string;
  cause: ApiNotificationCause[] | null;
  pageParam?: number | null | undefined;
  signal?: AbortSignal | undefined;
};
const getIdentityNotificationsQueryKey = (identity: string | null | undefined, limit: string, cause: ApiNotificationCause[] | null) => [QueryKey.IDENTITY_NOTIFICATIONS, {
  identity,
  limit,
  cause
}] as const;
const fetchNotifications = async ({
  limit,
  cause,
  pageParam,
  signal
}: NotificationsQueryParams) => {
  const params: Record<string, string> = {
    limit
  };
  if (pageParam != null) {
    params["id_less_than"] = String(pageParam);
  }
  if (cause?.length) {
    params["cause"] = cause.join(",");
  }
  return await commonApiFetch<TypedNotificationsResponse>({
    endpoint: "notifications",
    params,
    signal
  });
};
export function useNotificationsQuery(t0) {
  const $ = _c(26);
  const {
    reverse: t1,
    identity,
    activeProfileProxy: t2,
    limit: t3,
    cause: t4
  } = t0;
  const reverse = t1 === undefined ? false : t1;
  const activeProfileProxy = t2 === undefined ? false : t2;
  const limit = t3 === undefined ? "30" : t3;
  const cause = t4 === undefined ? null : t4;
  const prefetch = usePrefetchNotifications();
  let t5;
  let t6;
  if ($[0] !== activeProfileProxy || $[1] !== cause || $[2] !== identity || $[3] !== limit || $[4] !== prefetch) {
    t5 = () => {
      if (!identity || activeProfileProxy) {
        return;
      }
      prefetch({
        identity,
        limit,
        cause,
        pages: 1
      });
    };
    t6 = [prefetch, identity, activeProfileProxy, limit, cause];
    $[0] = activeProfileProxy;
    $[1] = cause;
    $[2] = identity;
    $[3] = limit;
    $[4] = prefetch;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] !== cause || $[8] !== identity || $[9] !== limit) {
    t7 = getIdentityNotificationsQueryKey(identity, limit, cause);
    $[7] = cause;
    $[8] = identity;
    $[9] = limit;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== cause || $[12] !== limit) {
    t8 = t9 => {
      const {
        pageParam,
        signal
      } = t9;
      return fetchNotifications({
        limit,
        cause,
        pageParam,
        signal
      });
    };
    $[11] = cause;
    $[12] = limit;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = !!identity && !activeProfileProxy;
  let t10;
  if ($[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      initialPageParam: null,
      getNextPageParam: _temp,
      enabled: t9,
      staleTime: 60000,
      retry: _temp2
    };
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const query = useInfiniteQuery(t10);
  let t11;
  bb0: {
    if (!query.data) {
      let t12;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = [];
        $[18] = t12;
      } else {
        t12 = $[18];
      }
      t11 = t12;
      break bb0;
    }
    const t12 = query.data.pages as TypedNotificationsResponse[];
    let t13;
    if ($[19] !== reverse || $[20] !== t12) {
      const data = t12.flatMap(_temp3);
      t13 = reverse ? [...data].reverse() : data;
      $[19] = reverse;
      $[20] = t12;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    t11 = t13;
  }
  const items = t11;
  const isInitialQueryDone = query.isSuccess || query.isError;
  let t12;
  if ($[22] !== isInitialQueryDone || $[23] !== items || $[24] !== query) {
    t12 = {
      ...query,
      items,
      isInitialQueryDone
    };
    $[22] = isInitialQueryDone;
    $[23] = items;
    $[24] = query;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}
function _temp3(page) {
  return page.notifications;
}
function _temp2(failureCount, error) {
  const status = (error as any)?.status ?? (error as any)?.response?.status ?? (error as any)?.cause?.status;
  if (status === 401) {
    return false;
  }
  if (typeof error === "string" && /unauthorized/i.test(error)) {
    return false;
  }
  if (error instanceof Error && /unauthorized/i.test(error.message)) {
    return false;
  }
  return failureCount < 3;
}
function _temp(lastPage) {
  return lastPage.notifications.at(-1)?.id ?? null;
}
export function usePrefetchNotifications() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = t1 => {
      const {
        identity,
        cause: t2,
        limit: t3,
        pages: t4
      } = t1;
      const cause = t2 === undefined ? null : t2;
      const limit = t3 === undefined ? "30" : t3;
      const pages = t4 === undefined ? 3 : t4;
      if (!identity) {
        return;
      }
      queryClient.prefetchInfiniteQuery({
        queryKey: getIdentityNotificationsQueryKey(identity, limit, cause),
        queryFn: t5 => {
          const {
            pageParam,
            signal
          } = t5;
          return fetchNotifications({
            limit,
            cause,
            pageParam,
            signal
          });
        },
        initialPageParam: null,
        getNextPageParam: _temp4,
        pages,
        staleTime: 60000
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp4(lastPage) {
  return lastPage.notifications.at(-1)?.id ?? null;
}